<template>
  <div>
    <div v-if="loading" class="muted">{{ $t('common.loading') }}</div>
    <div v-else :class="['att-grid', compact ? 'att-grid--compact' : '']">
      <div v-for="f in infos" :key="f.id" :class="['att-card', 'glass-card', compact ? 'att-card--compact' : '']" @click="onClick(f)">
        <div class="att-thumb">
          <img v-if="isImage(f)" class="att-media" :src="fileUrl(f)" alt="" />
          <img v-else-if="isVideo(f) && thumbUrl(f)" class="att-media" :src="thumbUrl(f)" alt="" />
          <div v-else class="att-icon">
            <i :class="fileIcon(f)" />
          </div>
          <div v-if="isVideo(f)" class="att-badge"><i class="el-icon-video-play" /></div>
          <div v-else-if="!f.missing && !isImage(f)" class="att-badge" @click.stop="downloadFile(f)"><i class="el-icon-download" /></div>
        </div>
        <div :class="['att-name', f.missing ? 'att-name--missing' : '']">{{ f.originalName || ('#' + f.id) }}</div>
      </div>
    </div>

    <el-dialog
      :title="previewTitle"
      :visible.sync="previewOpen"
      width="92vw"
      top="4vh"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="true"
      :append-to-body="true"
      custom-class="att-preview-dialog"
    >
      <div v-if="previewFile && previewType==='image'" style="text-align:center;">
        <img :src="fileUrl(previewFile)" style="max-width:100%; max-height: 70vh; border-radius: 14px;" alt="" />
      </div>
      <div v-else-if="previewFile && previewType==='video'">
        <video :src="videoUrl(previewFile)" controls style="width:100%; border-radius: 14px; max-height: 70vh;" />
      </div>
      <div v-else-if="previewFile && previewType==='file'" class="file-preview">
        <div class="file-preview__icon"><i class="el-icon-document" /></div>
        <div style="margin-top:10px; font-weight:600;">{{ previewFile.originalName || ('#' + previewFile.id) }}</div>
        <div class="muted" style="margin-top:6px; font-size:12px; line-height:1.6;">
          {{ previewFile.contentType || '-' }}<br />
          {{ formatSize(previewFile.size) }}
        </div>
        <div style="margin-top:12px;">
          <el-button type="primary" class="btn-glass" @click="downloadFile(previewFile)">{{ $t('common.download') }}</el-button>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="previewOpen=false">{{ $t('common.cancel') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fileInfo } from '../api/files';
import { withApiBase } from '../api/base';

export default {
  name: 'AttachmentGallery',
  props: {
    fileIds: { type: Array, default: () => [] },
    // When used inside chat bubbles, keep thumbnails compact to avoid huge empty spaces.
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      infos: [],
      previewOpen: false,
      previewFile: null,
      previewType: ''
    };
  },
  computed: {
    previewTitle() {
      return this.previewFile ? this.previewFile.originalName || `#${this.previewFile.id}` : '';
    }
  },
  watch: {
    fileIds: {
      deep: true,
      immediate: true,
      handler() {
        this.load();
      }
    }
  },
  methods: {
    async load() {
      const ids = this.fileIds || [];
      if (!ids || ids.length === 0) {
        this.infos = [];
        return;
      }
      this.loading = true;
      try {
        this.infos = (await Promise.all(ids.map((id) => fileInfo(id).catch(() => ({
          id,
          originalName: this.$t('common.attachmentUnavailable'),
          missing: true
        }))))).filter(Boolean);
      } finally {
        this.loading = false;
      }
    },
    isImage(f) {
      const ct = (f && f.contentType ? String(f.contentType) : '').toLowerCase();
      // Never treat SVG as an "image preview" to avoid rendering executable content inline.
      if (ct.startsWith('image/svg')) return false;
      return ct.startsWith('image/');
    },
    isVideo(f) {
      const ct = (f && f.contentType ? String(f.contentType) : '').toLowerCase();
      if (ct.startsWith('video/')) return true;
      const n = (f && f.originalName ? String(f.originalName) : '').toLowerCase();
      return n.endsWith('.mp4') || n.endsWith('.mov') || n.endsWith('.avi') || n.endsWith('.mkv') || n.endsWith('.webm');
    },
    fileUrl(f) {
      return f && f.id != null ? withApiBase(`/api/files/${f.id}`) : '#';
    },
    videoUrl(f) {
      if (!f || f.id == null) return '#';
      if (f.transcodeStatus === 'DONE') return withApiBase(`/api/files/${f.id}?variant=transcoded`);
      return withApiBase(`/api/files/${f.id}`);
    },
    thumbUrl(f) {
      if (!f || f.id == null) return '';
      if (f.hasThumb) return withApiBase(`/api/files/${f.id}?variant=thumb`);
      return '';
    },
    fileIcon() {
      return 'el-icon-document';
    },
    formatSize(n) {
      if (!n && n !== 0) return '-';
      const kb = n / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      const mb = kb / 1024;
      if (mb < 1024) return `${mb.toFixed(1)} MB`;
      const gb = mb / 1024;
      return `${gb.toFixed(1)} GB`;
    },
    onClick(f) {
      if (f && f.missing) return;
      if (this.isImage(f)) {
        this.previewFile = f;
        this.previewType = 'image';
        this.previewOpen = true;
        return;
      }
      if (this.isVideo(f)) {
        this.previewFile = f;
        this.previewType = 'video';
        this.previewOpen = true;
        return;
      }
      if (f && f.id != null) {
        this.previewFile = f;
        this.previewType = 'file';
        this.previewOpen = true;
      }
    },
    async downloadFile(f) {
      if (!f || f.id == null || f.missing) return;
      const name = (f.originalName || `file_${f.id}`).replace(/[\\\\/:*?"<>|]/g, '_');
      // Use direct navigation (not XHR) so it works both for local downloads and for future S3 signed URL redirects
      // without requiring CORS on the storage domain.
      const a = document.createElement('a');
      a.href = withApiBase(`/api/files/${f.id}`);
      a.download = name;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }
};
</script>

<style scoped>
.att-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
.att-grid--compact {
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
}
.att-card {
  padding: 10px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease;
}
.att-card--compact {
  padding: 8px;
}
.att-card:hover {
  transform: translateY(-1px);
}
.att-thumb {
  position: relative;
  width: 100%;
  height: 88px;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.55);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.att-card--compact .att-thumb {
  height: 74px;
  border-radius: 12px;
}
.att-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.att-icon {
  font-size: 26px;
  color: rgba(30, 41, 59, 0.85);
}
.att-badge {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 26px;
  height: 26px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(226, 232, 240, 0.9);
  color: rgba(30, 41, 59, 0.9);
  cursor: pointer;
}
.att-name {
  margin-top: 8px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.att-name--missing {
  color: rgba(100, 116, 139, 0.9);
}
.att-card--compact .att-name {
  margin-top: 6px;
  font-size: 12px;
}

.file-preview {
  text-align: center;
  padding: 10px 0;
}
.file-preview__icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.55);
  font-size: 26px;
  color: rgba(30, 41, 59, 0.9);
}
</style>
